<div class="space-y-6">
  <!-- Welcome & Date Header -->
  <div
    class="daisy-card bg-gradient-to-br from-primary/10 via-base-100 to-secondary/10 border border-base-300 shadow-sm"
  >
    <div class="daisy-card-body p-5 sm:p-6">
      <div
        class="flex flex-col lg:flex-row lg:items-center justify-between gap-4"
      >
        <div class="flex items-center gap-4">
          <div class="daisy-avatar daisy-placeholder">
            <div
              class="bg-primary text-primary-content rounded-2xl w-14 h-14 font-bold text-xl shadow-inner"
            >
              <span>{{ userInitial }}</span>
            </div>
          </div>
          <div>
            <div class="flex items-center gap-2 flex-wrap">
              <span
                class="text-xs uppercase tracking-wider text-base-content/60 font-semibold"
                >Welcome back</span
              >
              @if (isInstructor) {
                <span class="daisy-badge daisy-badge-primary daisy-badge-sm font-medium"
                  >Instructor</span
                >
              } @else {
                <span class="daisy-badge daisy-badge-outline daisy-badge-sm font-medium"
                  >Student</span
                >
              }
            </div>
            <h2 class="text-2xl sm:text-3xl font-extrabold text-base-content mt-0.5">
              {{ fullName }}
            </h2>
          </div>
        </div>

        <div class="flex flex-wrap items-center gap-3">
          <!-- Role Switcher for Instructors who also learn -->
          @if (isInstructor) {
            <div
              class="daisy-join bg-base-200/90 p-1 rounded-xl border border-base-300 shadow-sm"
            >
              <button
                type="button"
                (click)="setRoleTab('TEACHING')"
                class="daisy-btn daisy-btn-xs sm:daisy-btn-sm daisy-join-item font-semibold"
                [class.daisy-btn-primary]="activeRoleTab === 'TEACHING'"
                [class.daisy-btn-ghost]="activeRoleTab !== 'TEACHING'"
              >
                <i class="fa-solid fa-person-chalkboard mr-1.5"></i>
                <span>Teaching Hub</span>
              </button>
              <button
                type="button"
                (click)="setRoleTab('LEARNING')"
                class="daisy-btn daisy-btn-xs sm:daisy-btn-sm daisy-join-item font-semibold"
                [class.daisy-btn-primary]="activeRoleTab === 'LEARNING'"
                [class.daisy-btn-ghost]="activeRoleTab !== 'LEARNING'"
              >
                <i class="fa-solid fa-graduation-cap mr-1.5"></i>
                <span>My Learning</span>
              </button>
            </div>
          }

          <div
            class="px-3.5 py-1.5 rounded-xl bg-base-200/80 border border-base-300/80 text-xs sm:text-sm font-medium text-base-content/80 flex items-center gap-2"
          >
            <i class="fa-regular fa-calendar text-primary"></i>
            <span>{{ todayDateString }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- ========================================== -->
  <!-- HERO BANNER: NEXT ACTION (TEACH OR LEARN)  -->
  <!-- ========================================== -->
  @if (isLoading) {
    <div class="daisy-card bg-base-100 border border-base-300 shadow-sm p-6">
      <div class="flex items-center gap-4">
        <div class="daisy-skeleton w-12 h-12 rounded-xl shrink-0"></div>
        <div class="space-y-2 flex-1">
          <div class="daisy-skeleton h-4 w-1/3"></div>
          <div class="daisy-skeleton h-4 w-1/2"></div>
        </div>
      </div>
    </div>
  } @else if (currentHeroSession) {
    @if (currentHeroSession.isLive || currentHeroSession.canJoin) {
      <!-- Urgent Action: Session Happening Now -->
      <div
        class="daisy-card bg-gradient-to-r from-error/15 via-base-100 to-error/10 border-2 border-error/40 shadow-md"
      >
        <div class="daisy-card-body p-5 sm:p-6">
          <div
            class="flex flex-col md:flex-row md:items-center justify-between gap-4"
          >
            <div class="flex items-start sm:items-center gap-4">
              <div
                class="w-12 h-12 rounded-2xl bg-error text-error-content flex items-center justify-center shrink-0 shadow-md animate-pulse"
              >
                <i class="fa-solid fa-video text-xl"></i>
              </div>
              <div>
                <div class="flex items-center gap-2 mb-1 flex-wrap">
                  <span
                    class="daisy-badge daisy-badge-error text-error-content font-bold text-xs gap-1.5 animate-pulse"
                  >
                    <i class="fa-solid fa-circle text-[7px]"></i>
                    {{ isTeachingTab ? 'CLASS IN PROGRESS' : 'LIVE NOW' }}
                  </span>
                  <span class="text-xs text-base-content/70 font-medium">
                    {{ currentHeroSession.liveClassName }}
                  </span>
                </div>
                <h3 class="text-lg sm:text-xl font-bold text-base-content">
                  {{ currentHeroSession.meetingName }}
                </h3>
                <p class="text-xs text-base-content/60 mt-0.5">
                  <i class="fa-regular fa-clock text-error mr-1"></i>
                  {{ formatSessionTime(currentHeroSession.startsAt, currentHeroSession.durationInMinutes) }}
                </p>
              </div>
            </div>

            <div class="flex items-center gap-2.5 shrink-0 pt-2 md:pt-0">
              <button
                type="button"
                (click)="launchSession(currentHeroSession)"
                class="daisy-btn daisy-btn-error text-error-content font-bold gap-2 shadow-md hover:scale-[1.02] active:scale-[0.98] transition-transform"
              >
                <i class="fa-solid fa-play"></i>
                <span>{{ isTeachingTab ? 'Start / Host Class Now' : 'Join Class Now' }}</span>
              </button>
              <a
                [routerLink]="isTeachingTab ? ['/dashboard/instructor/live-class', currentHeroSession.liveClassId] : ['/dashboard/learning/live-class', currentHeroSession.liveClassId]"
                class="daisy-btn daisy-btn-ghost daisy-btn-sm sm:daisy-btn-md gap-1.5"
              >
                <span>{{ isTeachingTab ? 'Class Details' : 'Details' }}</span>
                <i class="fa-solid fa-arrow-right text-xs"></i>
              </a>
            </div>
          </div>
        </div>
      </div>
    } @else {
      <!-- Upcoming Session -->
      <div
        class="daisy-card bg-base-100 border border-primary/30 shadow-sm hover:border-primary/60 transition-colors"
      >
        <div class="daisy-card-body p-5 sm:p-6">
          <div
            class="flex flex-col sm:flex-row sm:items-center justify-between gap-4"
          >
            <div class="flex items-start sm:items-center gap-4">
              <div
                class="w-12 h-12 rounded-2xl bg-primary/10 text-primary flex items-center justify-center shrink-0"
              >
                <i [class]="isTeachingTab ? 'fa-solid fa-person-chalkboard text-xl' : 'fa-solid fa-calendar-check text-xl'"></i>
              </div>
              <div>
                <div class="flex items-center gap-2 mb-1 flex-wrap">
                  <span
                    class="daisy-badge daisy-badge-primary daisy-badge-sm font-semibold gap-1"
                  >
                    <i class="fa-solid fa-stopwatch text-xs"></i>
                    {{ currentHeroSession.countdown.countdownText }}
                  </span>
                  <span class="text-xs text-base-content/70 font-medium">
                    {{ currentHeroSession.liveClassName }}
                  </span>
                </div>
                <h3 class="text-lg font-bold text-base-content">
                  {{ currentHeroSession.meetingName }}
                </h3>
                <p class="text-xs text-base-content/60 mt-0.5">
                  <i class="fa-regular fa-clock text-primary mr-1"></i>
                  {{ formatSessionTime(currentHeroSession.startsAt, currentHeroSession.durationInMinutes) }}
                </p>
              </div>
            </div>

            <div class="flex items-center gap-2 shrink-0 pt-2 sm:pt-0">
              @if (isTeachingTab) {
                <button
                  type="button"
                  (click)="launchSession(currentHeroSession)"
                  class="daisy-btn daisy-btn-primary daisy-btn-sm sm:daisy-btn-md gap-2"
                >
                  <i class="fa-solid fa-video"></i>
                  <span>Host Session</span>
                </button>
                <a
                  [routerLink]="['/dashboard/instructor/live-class', currentHeroSession.liveClassId]"
                  class="daisy-btn daisy-btn-ghost daisy-btn-sm sm:daisy-btn-md gap-1.5"
                >
                  <span>Class Room</span>
                </a>
              } @else {
                <a
                  routerLink="/dashboard/meetings"
                  class="daisy-btn daisy-btn-primary daisy-btn-sm sm:daisy-btn-md gap-2"
                >
                  <i class="fa-solid fa-calendar-days"></i>
                  <span>View in Schedule</span>
                </a>
                <a
                  [routerLink]="['/dashboard/learning/live-class', currentHeroSession.liveClassId]"
                  class="daisy-btn daisy-btn-ghost daisy-btn-sm sm:daisy-btn-md gap-1.5"
                >
                  <span>Class Room</span>
                </a>
              }
            </div>
          </div>
        </div>
      </div>
    }
  } @else {
    <!-- Friendly Resting Banner -->
    <div
      class="daisy-card bg-base-100 border border-base-300/80 shadow-sm p-4 sm:p-5"
    >
      <div
        class="flex flex-col sm:flex-row sm:items-center justify-between gap-3"
      >
        <div class="flex items-center gap-3">
          <div
            class="w-10 h-10 rounded-xl bg-base-200 text-base-content/70 flex items-center justify-center shrink-0"
          >
            <i [class]="isTeachingTab ? 'fa-solid fa-calendar-check text-base' : 'fa-solid fa-calendar-day text-base'"></i>
          </div>
          <div>
            <h4 class="text-sm font-bold text-base-content">
              {{ isTeachingTab ? 'No classes scheduled to teach today' : 'No live classes scheduled for today' }}
            </h4>
            <p class="text-xs text-base-content/60">
              {{ isTeachingTab ? 'Create new class schedules, organize student cohorts, or review curriculum.' : 'Catch up on course lectures, assigned batches, or check your upcoming schedule.' }}
            </p>
          </div>
        </div>
        <div class="flex items-center gap-2 shrink-0">
          @if (isTeachingTab) {
            <a
              routerLink="/live-classes/add"
              class="daisy-btn daisy-btn-primary daisy-btn-sm gap-1.5"
            >
              <i class="fa-solid fa-plus text-xs"></i>
              <span>Schedule Class</span>
            </a>
          }
          <a
            routerLink="/dashboard/meetings"
            class="daisy-btn daisy-btn-ghost daisy-btn-sm text-primary gap-1.5"
          >
            <span>{{ isTeachingTab ? 'Calendar' : 'View Calendar' }}</span>
            <i class="fa-solid fa-arrow-right text-xs"></i>
          </a>
        </div>
      </div>
    </div>
  }

  <!-- ========================================== -->
  <!-- INSTRUCTOR VIEW: TEACHING & MANAGEMENT HUB -->
  <!-- ========================================== -->
  @if (isInstructor && activeRoleTab === 'TEACHING') {

    <!-- The 4 Teaching & Content Pillars -->
    <div>
      <div class="flex items-center justify-between mb-3">
        <div>
          <h3 class="text-lg font-bold text-base-content">Teaching & Content</h3>
          <p class="text-xs text-base-content/60">
            Manage your live classrooms, student cohorts, video courses, and enrolled students
          </p>
        </div>
      </div>

      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <!-- Pillar 1: Live Classes -->
        <div
          class="daisy-card bg-base-100 border border-base-300 shadow-sm hover:shadow-md hover:border-primary/60 transition-all duration-200 flex flex-col justify-between"
        >
          <div class="daisy-card-body p-5">
            <div class="flex items-start justify-between gap-2 mb-3">
              <div
                class="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0"
              >
                <i class="fa-solid fa-person-chalkboard text-xl"></i>
              </div>
              @if (isLoading) {
                <div class="daisy-skeleton h-5 w-16 rounded-full"></div>
              } @else {
                <span class="daisy-badge daisy-badge-primary daisy-badge-sm font-semibold">
                  {{ instructorClassesCount }} {{ instructorClassesCount === 1 ? 'Class' : 'Classes' }}
                </span>
              }
            </div>
            <div>
              <h4 class="font-bold text-base text-base-content">Live Classes</h4>
              <p class="text-xs text-base-content/65 mt-1 leading-relaxed">
                Live interactive streams, schedules, and classroom resources.
              </p>
            </div>
            <div class="pt-4 mt-auto flex flex-col gap-2">
              <a
                routerLink="/dashboard/instructor"
                class="daisy-btn daisy-btn-primary daisy-btn-sm w-full gap-2"
              >
                <span>Manage Classes</span>
                <i class="fa-solid fa-arrow-right text-xs"></i>
              </a>
              <a
                routerLink="/live-classes/add"
                class="daisy-btn daisy-btn-ghost daisy-btn-xs text-primary gap-1.5"
              >
                <i class="fa-solid fa-plus text-xs"></i>
                <span>Schedule New</span>
              </a>
            </div>
          </div>
        </div>

        <!-- Pillar 2: Batches -->
        <div
          class="daisy-card bg-base-100 border border-base-300 shadow-sm hover:shadow-md hover:border-secondary/60 transition-all duration-200 flex flex-col justify-between"
        >
          <div class="daisy-card-body p-5">
            <div class="flex items-start justify-between gap-2 mb-3">
              <div
                class="w-12 h-12 rounded-xl bg-secondary/10 text-secondary flex items-center justify-center shrink-0"
              >
                <i class="fa-solid fa-users-rectangle text-xl"></i>
              </div>
              @if (isLoading) {
                <div class="daisy-skeleton h-5 w-16 rounded-full"></div>
              } @else {
                <span class="daisy-badge daisy-badge-secondary daisy-badge-sm font-semibold">
                  {{ instructorBatchesCount }} {{ instructorBatchesCount === 1 ? 'Batch' : 'Batches' }}
                </span>
              }
            </div>
            <div>
              <h4 class="font-bold text-base text-base-content">Batches</h4>
              <p class="text-xs text-base-content/65 mt-1 leading-relaxed">
                Student cohorts, shared class schedules, and content bundles.
              </p>
            </div>
            <div class="pt-4 mt-auto flex flex-col gap-2">
              <a
                routerLink="/dashboard/batches"
                class="daisy-btn daisy-btn-secondary daisy-btn-sm w-full gap-2"
              >
                <span>Manage Batches</span>
                <i class="fa-solid fa-arrow-right text-xs"></i>
              </a>
            </div>
          </div>
        </div>

        <!-- Pillar 3: Courses (if showCourses) -->
        @if (showCourses) {
          <div
            class="daisy-card bg-base-100 border border-base-300 shadow-sm hover:shadow-md hover:border-accent/60 transition-all duration-200 flex flex-col justify-between"
          >
            <div class="daisy-card-body p-5">
              <div class="flex items-start justify-between gap-2 mb-3">
                <div
                  class="w-12 h-12 rounded-xl bg-accent/10 text-accent flex items-center justify-center shrink-0"
                >
                  <i class="fa-solid fa-graduation-cap text-xl"></i>
                </div>
                @if (isLoading) {
                  <div class="daisy-skeleton h-5 w-16 rounded-full"></div>
                } @else {
                  <span class="daisy-badge daisy-badge-accent daisy-badge-sm font-semibold">
                    {{ instructorCoursesCount }} {{ instructorCoursesCount === 1 ? 'Course' : 'Courses' }}
                  </span>
                }
              </div>
              <div>
                <h4 class="font-bold text-base text-base-content">Courses</h4>
                <p class="text-xs text-base-content/65 mt-1 leading-relaxed">
                  On-demand pre-recorded lessons, videos, and materials.
                </p>
              </div>
              <div class="pt-4 mt-auto flex flex-col gap-2">
                <a
                  routerLink="/dashboard/courses"
                  class="daisy-btn daisy-btn-accent daisy-btn-sm w-full gap-2"
                >
                  <span>Manage Courses</span>
                  <i class="fa-solid fa-arrow-right text-xs"></i>
                </a>
                <a
                  routerLink="/courses/add"
                  class="daisy-btn daisy-btn-ghost daisy-btn-xs text-accent gap-1.5"
                >
                  <i class="fa-solid fa-plus text-xs"></i>
                  <span>Add Course</span>
                </a>
              </div>
            </div>
          </div>
        }

        <!-- Pillar 4: Students -->
        <div
          class="daisy-card bg-base-100 border border-base-300 shadow-sm hover:shadow-md hover:border-emerald-500/60 transition-all duration-200 flex flex-col justify-between"
        >
          <div class="daisy-card-body p-5">
            <div class="flex items-start justify-between gap-2 mb-3">
              <div
                class="w-12 h-12 rounded-xl bg-emerald-100 text-emerald-600 flex items-center justify-center shrink-0"
              >
                <i class="fa-solid fa-user-graduate text-xl"></i>
              </div>
              @if (isLoading) {
                <div class="daisy-skeleton h-5 w-16 rounded-full"></div>
              } @else {
                <span class="daisy-badge daisy-badge-sm bg-emerald-100 text-emerald-700 font-semibold border-none">
                  {{ totalStudentsCount }} {{ totalStudentsCount === 1 ? 'Student' : 'Students' }}
                </span>
              }
            </div>
            <div>
              <h4 class="font-bold text-base text-base-content">Students</h4>
              <p class="text-xs text-base-content/65 mt-1 leading-relaxed">
                Student roster, cohort enrollments, and email invites.
              </p>
            </div>
            <div class="pt-4 mt-auto flex flex-col gap-2">
              <a
                routerLink="/dashboard/students"
                class="daisy-btn daisy-btn-sm bg-emerald-600 hover:bg-emerald-700 text-white w-full gap-2 border-none"
              >
                <span>View Students</span>
                <i class="fa-solid fa-arrow-right text-xs"></i>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Instructor Workspace & Management Tools -->
    <div>
      <h3 class="text-lg font-bold text-base-content mb-1">
        Workspace & Operations
      </h3>
      <p class="text-xs text-base-content/60 mb-3">
        Schedules, payouts, app integrations, and site settings
      </p>

      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <!-- Calendar & Schedule -->
        <a
          routerLink="/dashboard/meetings"
          class="daisy-card bg-base-100 border border-base-300 shadow-sm hover:shadow-md hover:border-primary transition-all p-4 cursor-pointer"
        >
          <div class="flex items-center gap-3">
            <div
              class="w-10 h-10 rounded-xl bg-green-100 text-green-600 flex items-center justify-center shrink-0"
            >
              <i class="fa-solid fa-calendar-check text-base"></i>
            </div>
            <div>
              <h4 class="font-semibold text-sm text-base-content">Calendar</h4>
              <p class="text-xs text-base-content/60">Weekly schedule</p>
            </div>
          </div>
        </a>

        <!-- Earnings & Payouts -->
        <a
          routerLink="/dashboard/earnings"
          class="daisy-card bg-base-100 border border-base-300 shadow-sm hover:shadow-md hover:border-primary transition-all p-4 cursor-pointer"
        >
          <div class="flex items-center gap-3">
            <div
              class="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-600 flex items-center justify-center shrink-0"
            >
              <i class="fa-solid fa-money-bill-trend-up text-base"></i>
            </div>
            <div>
              <h4 class="font-semibold text-sm text-base-content">Earnings</h4>
              <p class="text-xs text-base-content/60">Revenue & payouts</p>
            </div>
          </div>
        </a>

        <!-- Integrations (Zoom / Video) -->
        <a
          routerLink="/dashboard/app-management"
          class="daisy-card bg-base-100 border border-base-300 shadow-sm hover:shadow-md hover:border-primary transition-all p-4 cursor-pointer"
        >
          <div class="flex items-center gap-3">
            <div
              class="w-10 h-10 rounded-xl bg-purple-100 text-purple-600 flex items-center justify-center shrink-0"
            >
              <i class="fa-solid fa-puzzle-piece text-base"></i>
            </div>
            <div>
              <h4 class="font-semibold text-sm text-base-content">Integrations</h4>
              <p class="text-xs text-base-content/60">Zoom, RTK & storage</p>
            </div>
          </div>
        </a>

        <!-- Past Session Recordings -->
        <a
          routerLink="/dashboard/past-recordings"
          class="daisy-card bg-base-100 border border-base-300 shadow-sm hover:shadow-md hover:border-primary transition-all p-4 cursor-pointer"
        >
          <div class="flex items-center gap-3">
            <div
              class="w-10 h-10 rounded-xl bg-rose-100 text-rose-600 flex items-center justify-center shrink-0"
            >
              <i class="fa-solid fa-circle-play text-base"></i>
            </div>
            <div>
              <h4 class="font-semibold text-sm text-base-content">Recordings</h4>
              <p class="text-xs text-base-content/60">Completed sessions</p>
            </div>
          </div>
        </a>

        <!-- Site Studio -->
        <a
          routerLink="/site-editor"
          class="daisy-card bg-base-100 border border-base-300 shadow-sm hover:shadow-md hover:border-primary transition-all p-4 cursor-pointer"
        >
          <div class="flex items-center gap-3">
            <div
              class="w-10 h-10 rounded-xl bg-indigo-100 text-indigo-600 flex items-center justify-center shrink-0"
            >
              <i class="fa-solid fa-wand-magic-sparkles text-base"></i>
            </div>
            <div>
              <h4 class="font-semibold text-sm text-base-content">Site Studio</h4>
              <p class="text-xs text-base-content/60">Branding & pages</p>
            </div>
          </div>
        </a>

        <!-- Academy Settings -->
        <a
          routerLink="/dashboard/academy-settings"
          class="daisy-card bg-base-100 border border-base-300 shadow-sm hover:shadow-md hover:border-primary transition-all p-4 cursor-pointer"
        >
          <div class="flex items-center gap-3">
            <div
              class="w-10 h-10 rounded-xl bg-amber-100 text-amber-600 flex items-center justify-center shrink-0"
            >
              <i class="fa-solid fa-sliders text-base"></i>
            </div>
            <div>
              <h4 class="font-semibold text-sm text-base-content">Settings</h4>
              <p class="text-xs text-base-content/60">Workspace preferences</p>
            </div>
          </div>
        </a>

        <!-- Payments Management -->
        <a
          routerLink="/dashboard/payments"
          class="daisy-card bg-base-100 border border-base-300 shadow-sm hover:shadow-md hover:border-primary transition-all p-4 cursor-pointer"
        >
          <div class="flex items-center gap-3">
            <div
              class="w-10 h-10 rounded-xl bg-blue-100 text-blue-600 flex items-center justify-center shrink-0"
            >
              <i class="fa-solid fa-money-bill-transfer text-base"></i>
            </div>
            <div>
              <h4 class="font-semibold text-sm text-base-content">Transactions</h4>
              <p class="text-xs text-base-content/60">Student payments</p>
            </div>
          </div>
        </a>

        <!-- Profile -->
        <a
          routerLink="/dashboard/profile"
          class="daisy-card bg-base-100 border border-base-300 shadow-sm hover:shadow-md hover:border-primary transition-all p-4 cursor-pointer"
        >
          <div class="flex items-center gap-3">
            <div
              class="w-10 h-10 rounded-xl bg-gray-100 text-gray-600 flex items-center justify-center shrink-0"
            >
              <i class="fa-solid fa-user text-base"></i>
            </div>
            <div>
              <h4 class="font-semibold text-sm text-base-content">Profile</h4>
              <p class="text-xs text-base-content/60">Account details</p>
            </div>
          </div>
        </a>
      </div>
    </div>
  }

  <!-- ========================================== -->
  <!-- STUDENT VIEW (OR INSTRUCTOR MY LEARNING)   -->
  <!-- ========================================== -->
  @if (!isInstructor || activeRoleTab === 'LEARNING') {
    <!-- Continue Learning: The 3 Core Learning Pillars -->
    <div>
      <div class="flex items-center justify-between mb-3">
        <div>
          <h3 class="text-lg font-bold text-base-content">Continue Learning</h3>
          <p class="text-xs text-base-content/60">
            Direct access to your enrolled cohorts, live interactive classes, and self-paced courses
          </p>
        </div>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <!-- Pillar 1: My Batches -->
        <div
          class="daisy-card bg-base-100 border border-base-300 shadow-sm hover:shadow-md hover:border-primary/60 transition-all duration-200 flex flex-col justify-between"
        >
          <div class="daisy-card-body p-5">
            <div class="flex items-start justify-between gap-2 mb-3">
              <div
                class="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0"
              >
                <i class="fa-solid fa-users-rectangle text-xl"></i>
              </div>
              @if (isLoading) {
                <div class="daisy-skeleton h-5 w-16 rounded-full"></div>
              } @else {
                <span class="daisy-badge daisy-badge-primary daisy-badge-sm font-semibold">
                  {{ batchesCount }} {{ batchesCount === 1 ? 'Batch' : 'Batches' }}
                </span>
              }
            </div>
            <div>
              <h4 class="font-bold text-base text-base-content">My Batches</h4>
              <p class="text-xs text-base-content/65 mt-1 leading-relaxed">
                Assigned student cohorts, unified schedule, and curriculum modules.
              </p>
            </div>
            <div class="pt-4 mt-auto">
              <a
                routerLink="/dashboard/my-batches"
                class="daisy-btn daisy-btn-primary daisy-btn-sm w-full gap-2"
              >
                <span>Open Batches</span>
                <i class="fa-solid fa-arrow-right text-xs"></i>
              </a>
            </div>
          </div>
        </div>

        <!-- Pillar 2: My Classes -->
        <div
          class="daisy-card bg-base-100 border border-base-300 shadow-sm hover:shadow-md hover:border-secondary/60 transition-all duration-200 flex flex-col justify-between"
        >
          <div class="daisy-card-body p-5">
            <div class="flex items-start justify-between gap-2 mb-3">
              <div
                class="w-12 h-12 rounded-xl bg-secondary/10 text-secondary flex items-center justify-center shrink-0"
              >
                <i class="fa-solid fa-chalkboard-user text-xl"></i>
              </div>
              @if (isLoading) {
                <div class="daisy-skeleton h-5 w-16 rounded-full"></div>
              } @else {
                <span class="daisy-badge daisy-badge-secondary daisy-badge-sm font-semibold">
                  {{ classesCount }} {{ classesCount === 1 ? 'Class' : 'Classes' }}
                </span>
              }
            </div>
            <div>
              <h4 class="font-bold text-base text-base-content">My Classes</h4>
              <p class="text-xs text-base-content/65 mt-1 leading-relaxed">
                Live interactive classrooms, lesson resources, and stream access.
              </p>
            </div>
            <div class="pt-4 mt-auto">
              <a
                routerLink="/dashboard/learning"
                class="daisy-btn daisy-btn-secondary daisy-btn-sm w-full gap-2"
              >
                <span>Go to Classes</span>
                <i class="fa-solid fa-arrow-right text-xs"></i>
              </a>
            </div>
          </div>
        </div>

        <!-- Pillar 3: My Courses (conditional on showCourses) -->
        @if (showCourses) {
          <div
            class="daisy-card bg-base-100 border border-base-300 shadow-sm hover:shadow-md hover:border-accent/60 transition-all duration-200 flex flex-col justify-between"
          >
            <div class="daisy-card-body p-5">
              <div class="flex items-start justify-between gap-2 mb-3">
                <div
                  class="w-12 h-12 rounded-xl bg-accent/10 text-accent flex items-center justify-center shrink-0"
                >
                  <i class="fa-solid fa-graduation-cap text-xl"></i>
                </div>
                @if (isLoading) {
                  <div class="daisy-skeleton h-5 w-16 rounded-full"></div>
                } @else {
                  <span class="daisy-badge daisy-badge-accent daisy-badge-sm font-semibold">
                    {{ coursesCount }} {{ coursesCount === 1 ? 'Course' : 'Courses' }}
                  </span>
                }
              </div>
              <div>
                <h4 class="font-bold text-base text-base-content">My Courses</h4>
                <p class="text-xs text-base-content/65 mt-1 leading-relaxed">
                  Pre-recorded on-demand video courses, chapters, and exercises.
                </p>
              </div>
              <div class="pt-4 mt-auto">
                <a
                  routerLink="/dashboard/my-courses"
                  class="daisy-btn daisy-btn-accent daisy-btn-sm w-full gap-2"
                >
                  <span>Resume Courses</span>
                  <i class="fa-solid fa-arrow-right text-xs"></i>
                </a>
              </div>
            </div>
          </div>
        }
      </div>
    </div>

    <!-- Quick Access & Utilities -->
    <div>
      <h3 class="text-lg font-bold text-base-content mb-1">Quick Access</h3>
      <p class="text-xs text-base-content/60 mb-3">
        Calendar schedule, explore catalogs, orders, and account settings
      </p>

      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <!-- Calendar & Meetings -->
        <a
          routerLink="/dashboard/meetings"
          class="daisy-card bg-base-100 border border-base-300 shadow-sm hover:shadow-md hover:border-primary transition-all duration-200 cursor-pointer"
        >
          <div class="daisy-card-body p-4">
            <div class="flex items-center gap-3">
              <div
                class="w-10 h-10 rounded-xl bg-green-100 text-green-600 flex items-center justify-center shrink-0"
              >
                <i class="fa-solid fa-calendar-check text-base"></i>
              </div>
              <div>
                <h4 class="font-semibold text-sm text-base-content">Meetings</h4>
                <p class="text-xs text-base-content/60">Weekly schedule</p>
              </div>
            </div>
          </div>
        </a>

        <!-- Explore Classes -->
        <a
          routerLink="/live-classes"
          class="daisy-card bg-base-100 border border-base-300 shadow-sm hover:shadow-md hover:border-primary transition-all duration-200 cursor-pointer"
        >
          <div class="daisy-card-body p-4">
            <div class="flex items-center gap-3">
              <div
                class="w-10 h-10 rounded-xl bg-purple-100 text-purple-600 flex items-center justify-center shrink-0"
              >
                <i class="fa-solid fa-compass text-base"></i>
              </div>
              <div>
                <h4 class="font-semibold text-sm text-base-content">Explore Classes</h4>
                <p class="text-xs text-base-content/60">Find active live sessions</p>
              </div>
            </div>
          </div>
        </a>

        <!-- Explore Courses (conditional) -->
        @if (showCourses) {
          <a
            routerLink="/courses"
            class="daisy-card bg-base-100 border border-base-300 shadow-sm hover:shadow-md hover:border-primary transition-all duration-200 cursor-pointer"
          >
            <div class="daisy-card-body p-4">
              <div class="flex items-center gap-3">
                <div
                  class="w-10 h-10 rounded-xl bg-amber-100 text-amber-600 flex items-center justify-center shrink-0"
                >
                  <i class="fa-solid fa-book-open-reader text-base"></i>
                </div>
                <div>
                  <h4 class="font-semibold text-sm text-base-content">Explore Courses</h4>
                  <p class="text-xs text-base-content/60">Browse recorded lessons</p>
                </div>
              </div>
            </div>
          </a>
        }

        <!-- Past Recordings -->
        <a
          routerLink="/dashboard/past-recordings"
          class="daisy-card bg-base-100 border border-base-300 shadow-sm hover:shadow-md hover:border-primary transition-all duration-200 cursor-pointer"
        >
          <div class="daisy-card-body p-4">
            <div class="flex items-center gap-3">
              <div
                class="w-10 h-10 rounded-xl bg-rose-100 text-rose-600 flex items-center justify-center shrink-0"
              >
                <i class="fa-solid fa-circle-play text-base"></i>
              </div>
              <div>
                <h4 class="font-semibold text-sm text-base-content">Recordings</h4>
                <p class="text-xs text-base-content/60">Past live sessions</p>
              </div>
            </div>
          </div>
        </a>

        <!-- Orders -->
        <a
          routerLink="/dashboard/orders"
          class="daisy-card bg-base-100 border border-base-300 shadow-sm hover:shadow-md hover:border-primary transition-all duration-200 cursor-pointer"
        >
          <div class="daisy-card-body p-4">
            <div class="flex items-center gap-3">
              <div
                class="w-10 h-10 rounded-xl bg-blue-100 text-blue-600 flex items-center justify-center shrink-0"
              >
                <i class="fa-solid fa-receipt text-base"></i>
              </div>
              <div>
                <h4 class="font-semibold text-sm text-base-content">Orders</h4>
                <p class="text-xs text-base-content/60">Purchases & receipts</p>
              </div>
            </div>
          </div>
        </a>

        <!-- Subscriptions -->
        <a
          routerLink="/dashboard/subscriptions"
          class="daisy-card bg-base-100 border border-base-300 shadow-sm hover:shadow-md hover:border-primary transition-all duration-200 cursor-pointer"
        >
          <div class="daisy-card-body p-4">
            <div class="flex items-center gap-3">
              <div
                class="w-10 h-10 rounded-xl bg-purple-100 text-purple-600 flex items-center justify-center shrink-0"
              >
                <i class="fa-solid fa-credit-card text-base"></i>
              </div>
              <div>
                <h4 class="font-semibold text-sm text-base-content">Subscriptions</h4>
                <p class="text-xs text-base-content/60">Active class plans</p>
              </div>
            </div>
          </div>
        </a>

        <!-- Invoices -->
        <a
          routerLink="/dashboard/invoices"
          class="daisy-card bg-base-100 border border-base-300 shadow-sm hover:shadow-md hover:border-primary transition-all duration-200 cursor-pointer"
        >
          <div class="daisy-card-body p-4">
            <div class="flex items-center gap-3">
              <div
                class="w-10 h-10 rounded-xl bg-yellow-100 text-yellow-600 flex items-center justify-center shrink-0"
              >
                <i class="fa-solid fa-file-invoice text-base"></i>
              </div>
              <div>
                <h4 class="font-semibold text-sm text-base-content">Invoices</h4>
                <p class="text-xs text-base-content/60">Billing & payments</p>
              </div>
            </div>
          </div>
        </a>

        <!-- Profile -->
        <a
          routerLink="/dashboard/profile"
          class="daisy-card bg-base-100 border border-base-300 shadow-sm hover:shadow-md hover:border-primary transition-all duration-200 cursor-pointer"
        >
          <div class="daisy-card-body p-4">
            <div class="flex items-center gap-3">
              <div
                class="w-10 h-10 rounded-xl bg-gray-100 text-gray-600 flex items-center justify-center shrink-0"
              >
                <i class="fa-solid fa-user text-base"></i>
              </div>
              <div>
                <h4 class="font-semibold text-sm text-base-content">Profile</h4>
                <p class="text-xs text-base-content/60">Settings & account</p>
              </div>
            </div>
          </div>
        </a>
      </div>
    </div>
  }
</div>

